import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment.development';
import { iEmpStationsResult } from './Interfaces/emp-stations';

type WorkspaceExpiryState = {
  active: boolean;
  message: string;
};
import {
  iNotificationSettingsDto,
  NotificationRight,
  NotificationRights,
} from './Interfaces/notification-settings';
import { UserSettingsDto } from './Interfaces/user-settings';
import { iUser } from './Interfaces/users';
import { StorageService } from './storage.service';
import { Nullable } from './Types/types';
import { ReleaseNotesSeenMeta } from './Interfaces/release-notes';
import { TerminologyType } from '../modules/i-team/tasks/index/utils/terminologyMap';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  public user$: Observable<iUser | undefined | null>;
  public employee$: Observable<any>;
  public customer$: Observable<any>;
  public role$: Observable<any>;
  public empType$: Observable<any>;
  public empStations$: Observable<iEmpStationsResult[]>;
  public permissions$: Observable<Array<string>>;
  public userSettings$: Observable<Nullable<UserSettingsDto>>;
  public token$: Observable<any>;
  public refreshToken$: Observable<string | null>;
  public companyId$: Observable<Nullable<string>>;
  public stationId$: Observable<Nullable<string>>;
  public beta$: Observable<Nullable<string>>;
  public notificationSettings$: Observable<iNotificationSettingsDto | null>;
  public releaseNotesSeen$: Observable<ReleaseNotesSeenMeta | null>;
  public taskTerminology$: Observable<TerminologyType>;
  public runningTaskId$: Observable<string | null>;
  public workspaceExpiry$: Observable<WorkspaceExpiryState | null>;
  public selectedTaskTeamId$: Observable<string | null>;
  public iTeamTeamsRefresh$: Observable<number>;

  public grt$: Observable<string | null>;
  public gat$: Observable<string | null>;
  public gate$: Observable<number | null>;
  public linkedAccount$: Observable<any>;

  private _userSource: BehaviorSubject<iUser | undefined | null>;
  private _employeeSource: BehaviorSubject<any>;
  private _customerSource: BehaviorSubject<any>;
  private _roleSource: BehaviorSubject<any>;
  private _empTypeSource: BehaviorSubject<any>;
  private _empStationsSource: BehaviorSubject<iEmpStationsResult[]>;
  private _permissionsSource: BehaviorSubject<Array<string>>;
  private _tokenSource: BehaviorSubject<any>;
  private _refreshTokenSource: BehaviorSubject<string | null>;
  private _userSettingsSource: BehaviorSubject<Nullable<UserSettingsDto>>;
  private _companyIdSource: BehaviorSubject<Nullable<string>>;
  private _stationIdSource: BehaviorSubject<Nullable<string>>;
  private _betaSource: BehaviorSubject<Nullable<string>>;
  private _notificationSettingsSource: BehaviorSubject<iNotificationSettingsDto | null>;
  private _releaseNotesSeenSource: BehaviorSubject<ReleaseNotesSeenMeta | null>;
  private _workspaceExpirySource: BehaviorSubject<WorkspaceExpiryState | null> = new BehaviorSubject<WorkspaceExpiryState | null>(null);
  private _taskTerminologySource = new BehaviorSubject<TerminologyType>('standard');
  private _runningTaskIdSource: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private _selectedTaskTeamIdSource: BehaviorSubject<string | null>;
  private _iTeamTeamsRefreshSource: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private _grtSource: BehaviorSubject<string | null>;
  private _gatSource: BehaviorSubject<string | null>;
  private _gateSource: BehaviorSubject<number | null>;
  private _linkedAccountSource: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);

  constructor(
    private _storageServ: StorageService,
    private _cookieServ: CookieService
  ) {
    this._userSource = new BehaviorSubject<iUser | undefined | null>(
      _storageServ.getItem('user')
    );
    this._customerSource = new BehaviorSubject<any>(
      _storageServ.getItem('customer')
    );
    this._employeeSource = new BehaviorSubject<any>(
      _storageServ.getItem('employee')
    );
    this._roleSource = new BehaviorSubject<any>(_storageServ.getItem('role'));
    this._empTypeSource = new BehaviorSubject<any>(
      _storageServ.getItem('empType')
    );
    this._empStationsSource = new BehaviorSubject<iEmpStationsResult[]>([]);
    this._permissionsSource = new BehaviorSubject<Array<string>>(
      _storageServ.getItem('permissions')
    );
    const token = _storageServ.getItem('token');
    this._tokenSource = new BehaviorSubject<any>(token);
    const refreshToken = _storageServ.getItem('refreshToken');
    this._refreshTokenSource = new BehaviorSubject<string | null>(refreshToken);
    this._userSettingsSource = new BehaviorSubject<Nullable<UserSettingsDto>>(
      _storageServ.getItem('userSettings')
    );
    this._companyIdSource = new BehaviorSubject<Nullable<string>>(undefined);
    this._stationIdSource = new BehaviorSubject<Nullable<string>>(undefined);
    this._betaSource = new BehaviorSubject<Nullable<string>>(undefined);
    this._notificationSettingsSource =
      new BehaviorSubject<iNotificationSettingsDto | null>(
        _storageServ.getItem('notificationSettings') || null
      );
    this._releaseNotesSeenSource =
      new BehaviorSubject<ReleaseNotesSeenMeta | null>(
        this._storageServ.getItem('releaseNotesSeen')
      );
    this._selectedTaskTeamIdSource = new BehaviorSubject<string | null>(
      this._storageServ.getItem('selectedTaskTeamId')
    );

    const grt = _storageServ.getItem('grt');
    this._grtSource = new BehaviorSubject<string | null>(grt);
    const gat = _storageServ.getItem('gat');
    this._gatSource = new BehaviorSubject<string | null>(gat);
    const gate = _storageServ.getItem('gate');
    this._gateSource = new BehaviorSubject<number | null>(
      gate ? parseInt(gate) : null
    );

    this.user$ = this._userSource.asObservable();
    this.employee$ = this._employeeSource.asObservable();
    this.customer$ = this._customerSource.asObservable();
    this.role$ = this._roleSource.asObservable();
    this.empType$ = this._empTypeSource.asObservable();
    this.empStations$ = this._empStationsSource.asObservable();
    this.permissions$ = this._permissionsSource.asObservable();
    this.token$ = this._tokenSource.asObservable();
    this.refreshToken$ = this._refreshTokenSource.asObservable();
    this.userSettings$ = this._userSettingsSource.asObservable();
    this.companyId$ = this._companyIdSource.asObservable();
    this.stationId$ = this._stationIdSource.asObservable();
    this.beta$ = this._betaSource.asObservable();
    this.notificationSettings$ =
      this._notificationSettingsSource.asObservable();
    this.releaseNotesSeen$ = this._releaseNotesSeenSource.asObservable();
    this.workspaceExpiry$ = this._workspaceExpirySource.asObservable();
    this.taskTerminology$ = this._taskTerminologySource.asObservable();
    this.runningTaskId$ = this._runningTaskIdSource.asObservable();
    this.selectedTaskTeamId$ = this._selectedTaskTeamIdSource.asObservable();
    this.iTeamTeamsRefresh$ = this._iTeamTeamsRefreshSource.asObservable();

    this.grt$ = this._grtSource.asObservable();
    this.gat$ = this._gatSource.asObservable();
    this.gate$ = this._gateSource.asObservable();
    this.linkedAccount$ = this._linkedAccountSource.asObservable();
  }

  get userValue() {
    return this._userSource.value;
  }
  get employeeValue() {
    return this._employeeSource.value;
  }
  get customerValue() {
    return this._customerSource.value;
  }
  get roleValue() {
    return this._roleSource.value;
  }
  get empTypeValue() {
    return this._empTypeSource.value;
  }
  get empStationsValue() {
    return this._empStationsSource.value;
  }
  get permissionsValue() {
    return this._permissionsSource.value;
  }
  get tokenValue() {
    return this._tokenSource.value;
  }
  get refreshTokenValue() {
    return this._refreshTokenSource.value;
  }
  get userSettingsValue() {
    return this._userSettingsSource.value;
  }
  get companyIdValue() {
    return this._companyIdSource.value;
  }
  get stationIdValue() {
    return this._stationIdSource.value;
  }
  get betaValue() {
    return this._betaSource.value;
  }
  get notificationSettingsValue() {
    return this._notificationSettingsSource.value;
  }
  get isSystemadmin() {
    if (!this.employeeValue) return false;
    return this.employeeValue.empRole == 'systemadmin';
  }

  get grtValue() {
    return this._grtSource.value;
  }
  get gatValue() {
    return this._gatSource.value;
  }
  get gateValue() {
    return this._gateSource.value;
  }
  get linkedAccountValue() {
    return this._linkedAccountSource.value;
  }
  get releaseNotesSeenValue() {
    return this._releaseNotesSeenSource.value;
  }
  get workspaceExpiryValue(): WorkspaceExpiryState | null {
    return this._workspaceExpirySource.value;
  }
  get taskTerminologyValue(): TerminologyType {
    return this._taskTerminologySource.value;
  }
  get runningTaskIdValue(): string | null {
    return this._runningTaskIdSource.value;
  }
  get selectedTaskTeamIdValue(): string | null {
    return this._selectedTaskTeamIdSource.value;
  }
  get iTeamTeamsRefreshValue(): number {
    return this._iTeamTeamsRefreshSource.value;
  }

  setUser(value: any) {
    this._storageServ.setItem('user', value);
    this.setRole(value?.role);

    this._userSource.next(value);
  }
  setEmployee(value: any, workspaceUrl?: Nullable<string>) {
    if (value.profilePic && !value.profilePic.includes(environment.base_url)) {
      value.profilePic = `${environment.base_url}/${value.profilePic}`;
    }
    value.name = value.firstName;
    if (value.lastName) value.name += ` ${value.lastName}`;
    let payload: any = {};
    if (this.betaValue) {
      payload = {
        ...this._storageServ.decryptValue(this.betaValue),
      };
    }
    payload = {
      ...payload,
      designation: value.designation,
      profilePicture: value.profilePic,
    };
    // if (workspaceUrl) {
    //   payload.uri = workspaceUrl;
    //   environment.web_url = workspaceUrl;
    // }
    this.setBeta(this._storageServ.encryptValue(payload));
    this._storageServ.setItem('employee', value);
    this.setEmpType(value?.type);
    this._employeeSource.next(value);
  }
  setCustomer(value: any) {
    const logo = value?.companyLogo;
    const hasValidLogo =
      typeof logo === 'string' &&
      logo.trim().length > 0 &&
      logo !== 'undefined' &&
      logo !== 'null';

    if (hasValidLogo) {
      const isAbsolute = /^https?:\/\//i.test(logo);
      const cleanedLogo = logo.split('?t=')[0];
      const logoUrl = isAbsolute
        ? cleanedLogo
        : `${environment.base_url}/${cleanedLogo}`;
      value.companyLogo = `${logoUrl}?t=${Date.now()}`;
    } else {
      value.companyLogo = null;
    }

    this._storageServ.setItem('customer', value);
    this._customerSource.next(value);
  }
  setEmpType(value: any) {
    this._storageServ.setItem('empType', value);
    this._empTypeSource.next(value);
  }
  setEmpStations(value: iEmpStationsResult[]) {
    this._empStationsSource.next(value);
  }
  setPermissions(value: Array<string>) {
    this._storageServ.setItem('permissions', value);
    this._permissionsSource.next(value);
  }
  setRole(value: any) {
    this._storageServ.setItem('role', value);
    this._roleSource.next(value);
  }
  setToken(value: any) {
    // if (environment.production) {
    //   const token = this._cookieServ.get('pookie');
    //   this._tokenSource.next(token);
    // } else {
    //   // this._cookieServ.set('pookie', value, {
    //   //   domain: environment.cookieDomain,
    //   //   path: '/',
    //   // });
    // // }
    this._storageServ.setItem('token', value);
    this._tokenSource.next(value);
  }
  setRefreshToken(value: any) {
    // if (environment.production) {
    //   const token = this._cookieServ.get('pookie');
    //   this._tokenSource.next(token);
    // } else {
    //   // this._cookieServ.set('pookie', value, {
    //   //   domain: environment.cookieDomain,
    //   //   path: '/',
    //   // });
    // // }
    this._storageServ.setItem('refreshToken', value);
    this._refreshTokenSource.next(value);
  }
  setUserSettings(value: Nullable<UserSettingsDto>) {
    this._storageServ.setItem('userSettings', value);
    this._userSettingsSource.next(value);
  }
  setCompanyId(value: Nullable<string>) {
    this._companyIdSource.next(value);
  }
  setStationId(value: Nullable<string>) {
    this._stationIdSource.next(value);
  }
  setBeta(value: Nullable<string>) {
    this._betaSource.next(value);
  }
  setNotificationSettings(value: iNotificationSettingsDto | null) {
    this._storageServ.setItem('notificationSettings', value);
    this._notificationSettingsSource.next(value);
  }
  setReleaseNotesSeen(value: ReleaseNotesSeenMeta | null) {
    this._storageServ.setItem('releaseNotesSeen', value);
    this._releaseNotesSeenSource.next(value);
  }
  setWorkspaceExpiry(value: WorkspaceExpiryState | null) {
    this._workspaceExpirySource.next(value);
  }
  setGrt(value: string | null) {
    this._storageServ.setItem('grt', value);
    this._grtSource.next(value);
  }
  setGat(value: string | null) {
    this._storageServ.setItem('gat', value);
    this._gatSource.next(value);
  }
  setGate(value: number | null) {
    this._storageServ.setItem('gate', value);
    this._gateSource.next(value);
  }
  setLinkedAccount(linkedAccount: any) {
    this._linkedAccountSource.next(linkedAccount);
  }
  setTaskTerminology(value: TerminologyType) {
    this._taskTerminologySource.next(value);
  }
  setRunningTaskId(taskId: string | null) {
    this._runningTaskIdSource.next(taskId);
  }
  setSelectedTaskTeamId(teamId: string | null) {
    if (!teamId) {
      this._storageServ.removeItem('selectedTaskTeamId');
      this._selectedTaskTeamIdSource.next(null);
      return;
    }

    this._storageServ.setItem('selectedTaskTeamId', teamId);
    this._selectedTaskTeamIdSource.next(teamId);
  }

  refreshITeamTeams() {
    this._iTeamTeamsRefreshSource.next(Date.now());
  }

  clear(alt: boolean = false) {
    this._userSource.next(null);
    this._employeeSource.next(null);
    this._customerSource.next(null);
    this._roleSource.next(null);
    this._empTypeSource.next(null);
    this._tokenSource.next(null);
    this._permissionsSource.next([]);
    this._companyIdSource.next(undefined);
    this._stationIdSource.next(undefined);
    this._notificationSettingsSource.next(null);
    this._releaseNotesSeenSource.next(null);
    this._workspaceExpirySource.next(null);
    this._runningTaskIdSource.next(null);
    this._selectedTaskTeamIdSource.next(null);
    this._iTeamTeamsRefreshSource.next(0);
    //alert('data.service.ts')
    this._storageServ.clear();
  }
  getNotificationRight(right: string): NotificationRights | null {
    if (!right) {
      return null;
    }
    const settings = this.notificationSettingsValue;
    if (!settings) return null;

    function findNodeById(
      rights: NotificationRight[],
      idToFind: string
    ): NotificationRights | null {
      for (const node of rights) {
        if (node.rightCode === idToFind) {
          return node; // Found the node
        }
        if (node.notificationRight) {
          const foundInChild = findNodeById(node.notificationRight, idToFind);
          if (foundInChild) {
            return foundInChild; // Found in a child branch
          }
        }
      }
      return null; // Not found in this branch
    }

    return findNodeById(settings.rights, right);
  }
  checkDomain(): boolean {
    return true;
    if (this.betaValue) {
      const data = this._storageServ.decryptValue(this.betaValue);

      if (data.uri) {
        return data.uri
          .toLowerCase()
          .includes(window.location.hostname.toLowerCase());
      } else return false;
    } else return false;
  }
  hasPermission(right: string | string[]): boolean {
    return this.checkPermission(this.roleValue, this.permissionsValue, right);
  }
  /**
   * Resolves the correct permission right based on the active module.
   * Use when the same feature has different right codes in hr360 vs i-nest.
   * @param isStartup - true when running inside the i-nest module
   * @param hr360Right - right code(s) used in the hr360 module
   * @param inestRight - right code(s) used in the i-nest module
   */
  hasModulePermission(
    isStartup: boolean,
    hr360Right: string | string[],
    inestRight: string | string[]
  ): boolean {
    return this.hasPermission(isStartup ? inestRight : hr360Right);
  }
  private checkPermission(
    role: string,
    permissions: Array<string>,
    right: string | string[]
  ): boolean {
    if (role !== 'employee') {
      return false;
    }
    if (this.employeeValue?.empRole == 'systemadmin') return true;
    const rights = Array.isArray(right) ? right : [right];

    if (!rights.length || rights.includes('')) {
      return true;
    }

    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }

    return rights.some((r) => permissions.includes(r));
  }
  private normalizeString(value: string): string {
    return value.toLowerCase().trim().replace(/\s+/g, '-');
  }
}
